<script setup lang="ts">
import { UserTypes } from "@/models/enums";
import { computed, ref, useTemplateRef, watch } from "vue";
import gsap from "gsap";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useIsMobile } from "@/composables/useIsMobile";
import MobileNavigation from "@/components/layout/MobileNavigation.vue";
import OrganisationSwitcher from "@/components/layout/OrganisationSwitcher.vue";
import { isMaster, type Membership } from "@/stores/modules/organisations";
import BrandWordmark from "@/components/layout/BrandWordmark.vue";
import { BRAND_NAME } from "@/utils/brand";
import CustomIcon from "@/components/custom/CustomIcon.vue";
import { Popover, Button, SelectButton } from "primevue";

import { useLanguage } from "@/composables/useLanguage";

const store = useStore();
const route = useRoute();
const router = useRouter();
const user = computed(() => store.getters["users/user"]);
const { t } = useI18n();
const isMobile = useIsMobile();
const letterRefs = ref<HTMLElement[]>([]);
const settingsPopover = useTemplateRef("settingsPopover");
const brandName = BRAND_NAME;
const collapseIndices = [1, 2, 5];
const letterWidth = ref(16);
const collapseSet = computed(() => new Set(collapseIndices));
const { selectedLanguage, changeLanguage } = useLanguage();

const isOwner = computed(
  () => (user.value?.usertype ?? 0) >= UserTypes.President
);

// Owners get a link to their organisation labelled with its name, so pull the
// organisation document in once we know which account they belong to.
const organisationTitle = computed(
  () => store.getters["organisations/title"] as string
);
const organisationLabel = computed(
  () => organisationTitle.value || t("OrganisationSettings")
);

watch(
  [isOwner, () => user.value?.account],
  ([owner, account]) => {
    if (owner && account != null)
      store.dispatch("organisations/fetchByAccountId", account);
  },
  { immediate: true }
);

// Someone in several organisations sees which one they are in under the
// brand, on the dashboard.
const memberships = computed(() => store.getters["organisations/memberships"] as Membership[]);
const inSeveral = computed(
  () =>
    Object.keys(user.value?.organisations ?? {}).filter(
      (key) => Number(key) > 0 && Number(key) !== Number(user.value?.account),
    ).length > 0,
);
watch(
  [inSeveral, () => user.value?.account],
  ([several]) => {
    if (several && user.value) store.dispatch("organisations/fetchMemberships", user.value).catch(() => {});
  },
  { immediate: true },
);
const dashboardOrganisation = computed(() => {
  if (route.name !== "home" || !inSeveral.value) return "";
  const active = memberships.value.find((m) => m.account === Number(user.value?.account));
  return active?.title || "";
});

const language = computed({
  get: () => (selectedLanguage.value === 0 ? "Dansk" : "English"),
  set: (value: string | null) => {
    if (value === null) {
      language.value = "Dansk";
      return;
    }
    changeLanguage(value === "Dansk" ? 0 : 1);
  },
});
const logoIsWide = ref(true);
const toggleLogo = () => {
  if (logoIsWide) {
  }
};

// language is derived from the current locale, which is initialized
// from localStorage (and later synced from Firestore on auth).

const animateLogo = () => {
  gsap.to(letterRefs.value[1], {
    width: logoIsWide.value ? 0 : 16,
    duration: 0.25,
    ease: "Power3.easeInOut",
  });
  gsap.to(letterRefs.value[2], {
    width: logoIsWide.value ? 0 : 13,
    duration: 0.25,
    ease: "Power3.easeInOut",
  });
  gsap.to(letterRefs.value[5], {
    width: logoIsWide.value ? 0 : 10,
    duration: 0.25,
    ease: "Power3.easeInOut",
  });
  //   gsap.to(letterRefs.value, {
  //     width: (i, el: any) => {
  //       const idx = i as number
  //       return (logoIsWide.value || !collapseSet.value.has(idx)) ? 12 : 0
  //     },
  //     duration: 0.2,
  //     ease: "Power2.easeInOut",
  //     stagger: 0.02,
  //   })
  logoIsWide.value = !logoIsWide.value;
};

const onLogOut = async () => {
  await store.dispatch("auth/logOut");
  //   router.push({ path: '/' })
  window.location.href = "/";
};
</script>

<template>
  <div class="flex items-center lg:gap-16 h-20 sm:h-24 p-4 sm:px-8">
    <RouterLink to="/" class="inline-flex flex-col items-start self-baseline">
      <!-- <label ref="wrapRef" class="text-2xl font-black">
        <span
          v-for="(ch, i) in brandName"
          :key="i"
          ref="letterRefs"
          class="inline-block overflow-hidden align-baseline"
        >
          <span class="inline-block">{{ ch }}</span>
        </span>
      </label> -->
      <span class="text-2xl font-black"><BrandWordmark :name="brandName" /></span>
      <span v-if="dashboardOrganisation" class="text-xs text-neutral-500 leading-tight truncate max-w-48">
        {{ dashboardOrganisation }}
      </span>
    </RouterLink>
    <div
      v-if="!isMobile"
      class="nav flex items-baseline self-baseline ~gap-4/24 md:gap-8 mx-auto xl:mx-0"
    >
      <RouterLink
        v-if="user?.usertype >= UserTypes.Admin"
        to="/"
        activeClass="active"
      >
        <span>{{ t("Events") }}</span>
      </RouterLink>

      <RouterLink
        v-if="user?.usertype >= UserTypes.Admin"
        to="/users"
        activeClass="active"
      >
        <span>{{ t("Users") }}</span>
      </RouterLink>

      <RouterLink
        v-if="user?.usertype >= UserTypes.Admin"
        to="/repertoire"
        activeClass="active"
      >
        <span>{{ t("Repertoire") }}</span>
      </RouterLink>
    </div>
    <div v-if="!isMobile" class="self-center">
      <button
        type="button"
        class="px-2"
        @click="settingsPopover?.toggle($event)"
      >
        <CustomIcon
          icon="user"
          class="stroke-[0.75px] stroke-neutral-500 size-6"
        />
      </button>
      <Popover ref="settingsPopover">
        <div class="flex flex-col items-start min-w-48">
          <!-- <Button severity="secondary" size="small" :label="t('Settings')" fluid /> -->

          <span class="text-slate-500 text-xs">{{ t("Account") }}:</span>
          <span class="text-sm">{{ user?.name }}</span>
          <hr class="my-2 w-full h-px" />
          <OrganisationSwitcher />
          <hr class="my-2 w-full h-px" />
          <div class="flex flex-col gap-2 w-full">
            <Button
              severity="secondary"
              variant="outlined"
              class="!justify-start"
              size="small"
              fluid
              :label="t('Profile')"
              @click="router.push('/profile')"
            />
            <Button
              v-if="isOwner"
              severity="secondary"
              variant="outlined"
              class="!justify-start"
              size="small"
              fluid
              :label="organisationLabel"
              @click="router.push('/organisation')"
            />
            <Button
              v-if="user?.usertype >= UserTypes.Admin"
              severity="secondary"
              variant="outlined"
              class="!justify-start"
              size="small"
              fluid
              :label="t('ScheduleTemplates')"
              @click="router.push('/organisation/templates')"
            />
            <Button
              v-if="isMaster(user)"
              severity="secondary"
              variant="outlined"
              class="!justify-start"
              size="small"
              fluid
              :label="t('PlatformOrganisations')"
              @click="router.push('/platform')"
            />
            <SelectButton
              v-model="language"
              :allowedEmpty="false"
              :options="['Dansk', 'English']"
              size="small"
              fluid
            />
            <Button
              severity="secondary"
              size="small"
              :label="t('logOut')"
              @click="onLogOut"
            />
          </div>
        </div>
      </Popover>
    </div>
    <div v-if="isMobile" class="self-center">
      <MobileNavigation />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.nav {
  a {
    @apply relative flex items-center gap-4 text-neutral-500 font-thin;
    &::before {
      @apply content-[''] absolute bottom-0.5 h-px bg-slate-400 w-0 transition-all duration-500;
    }
    &:hover::before {
      @apply w-full duration-100;
    }
    &.active {
      @apply text-black;
      &::before {
        @apply w-full;
      }
    }
  }
}
</style>
